import { flushSync } from 'react-dom'

/**
 * Change the page's layout as a movement rather than a jump
 * -------------------------------------------------------------
 * The conversation folding away or coming back, filling the window or giving
 * it back, the skills rail opening or closing (Huanrui, 2026-10-01): the panel
 * slides out of its edge, and the board slides with it.
 *
 * Done with the browser's own view transitions: it takes a picture of the page
 * before, applies the change, and moves each named part (project.css,
 * view-transition-name) from where it was to where it is now. The board is
 * moved as one picture for that quarter of a second, not redrawn on every
 * frame, which is what keeps it smooth on a board of large pictures.
 *
 * A browser without view transitions, or somebody who has asked for less
 * motion, simply gets the change.
 */
export function animateLayout(change: () => void, after?: () => void): void {
  const doc = document as Document & { startViewTransition?: (update: () => void) => unknown }
  const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  if (!doc.startViewTransition || still) {
    change()
    after?.()
    return
  }
  // `after` runs once the change is in the page but before the new picture
  // is taken (2026-10-22): anything put right there -- a scroll position --
  // is in the picture, instead of jumping once the movement has started.
  doc.startViewTransition(() => {
    flushSync(change)
    after?.()
  })
}
